import { Edit, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  useDeleteTenantMutation,
  useLazyGetAllAdminsQuery
} from '../../services/adminAuditsApi/adminAuditApi';
import CommonDialog from '../Common/Dialog';
import DeleteStudentDialog from '../CoOrdinator/DeleteStudentDialog';
import Pagination from '../ReUsableComponents/CMS/Pagination';

import EditTenant from './EditTenant';
import { OnboardingFormType } from './types';

const AllTenantsAudit = () => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [isEdit, setIsEdit] = useState(false);
  const [openStudentDeleteDialog, setOpenStudentDeleteDialog] = useState(false);

  const [selectedTenant, setSelectedTenant] = useState<OnboardingFormType>({
    tenantName: '',
    dbName: '',
    emailId: '',
    phoneNumber: '91',
    address: '',
    institutionName: '',
    noOfSeats: 0,
    dashboards: [9001],
    planInDays: 30,
    id: '',
    extendedPlanDays: null
  });
  const availableDashboards = [
    { id: 9001, name: 'Company Tests' },
    { id: 9002, name: 'Tech Interview' },
    { id: 9003, name: 'HR Interview' },
    { id: 9004, name: 'Code Sense AI' },
    { id: 9006, name: 'Schedule Test' },
    { id: 9007, name: 'Custom Written Test' },
    { id: 9008, name: 'Drive Test' },
    { id: 9009, name: 'Cloud Prep' }
  ];

  const [getallTenantsData, { data, isLoading, error }] =
    useLazyGetAllAdminsQuery();
  const [deleteTenant] = useDeleteTenantMutation();

  const fetchAllTenantsData = async () => {
    try {
      const result = await getallTenantsData({
        page,
        size
      }).unwrap();
    } catch (error) {
      console.error('Error fetching tenants data:', error);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    fetchAllTenantsData();
  }, [page, size]);

  useEffect(() => {
    fetchAllTenantsData();
  }, []);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='text-gray-600'>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
        <p className='text-red-600'>Error loading admin data</p>
      </div>
    );
  }

  const formatDate = (dateString: any) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (active: any) => {
    return active ? (
      <span className='rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800'>
        Active
      </span>
    ) : (
      <span className='rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800'>
        Inactive
      </span>
    );
  };

  const handleDeleteTenant = async () => {
    try {
      await deleteTenant(selectedTenant?.id).unwrap();
      toast.success('Tenant Deleted Sucessfully');
      setOpenStudentDeleteDialog(false);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className='w-full'>
      {/* <div className='mb-4'>
        <p className='mt-1 text-sm text-gray-600'>
          Total Tenants: {data?.data?.length || 0}
        </p>
      </div> */}

      <div className='overflow-x-auto rounded-lg border border-gray-200 shadow-sm'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Institution
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Dashboards
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Contact
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Plan Details
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Seats
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Plan Expiry
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Status
              </th>
              <th className='px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200 bg-white'>
            {data?.data?.map((admin: any) => (
              <tr key={admin.id} className='transition-colors hover:bg-gray-50'>
                <td className='whitespace-nowrap px-4 py-4'>
                  <div className='flex flex-col'>
                    <div className='text-sm font-medium text-gray-900'>
                      {admin.institutionName}
                    </div>
                    <div className='text-sm text-gray-500'>
                      {admin.tenantName}
                    </div>
                  </div>
                </td>
                <td className='px-4 py-4'>
                  <div className='max-w-xs text-sm text-gray-900'>
                    {admin.dashboards && admin.dashboards.length > 0 ? (
                      <div className='flex flex-wrap gap-1'>
                        {admin.dashboards.map((dashId: any) => {
                          const dashboard = availableDashboards.find(
                            d => d.id === dashId
                          );
                          return (
                            <span
                              key={dashId}
                              className='inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700'
                            >
                              {dashboard?.name || '-'}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className='text-xs text-gray-400'>
                        No dashboards
                      </span>
                    )}
                  </div>
                </td>
                <td className='px-4 py-4'>
                  <div className='flex flex-col text-sm'>
                    <span className='text-gray-900'>{admin.emailId}</span>
                    <span className='text-gray-500'>{admin.phoneNumber}</span>
                  </div>
                </td>
                <td className='whitespace-nowrap px-4 py-4'>
                  <div className='flex flex-col gap-1'>
                    <span className='text-xs text-gray-500'>
                      {admin.planInDays} day{admin.planInDays > 1 ? 's' : ''}
                    </span>
                  </div>
                </td>
                <td className='whitespace-nowrap px-4 py-4 text-sm text-gray-900'>
                  {admin.noOfSeats}
                </td>
                <td className='whitespace-nowrap px-4 py-4'>
                  <div className='text-sm text-gray-900'>
                    {formatDate(admin.planExpiryDate)}
                  </div>
                  <div className='text-xs text-gray-500'>
                    Started: {formatDate(admin.planTakenDate)}
                  </div>
                </td>
                <td className='whitespace-nowrap px-4 py-4'>
                  {getStatusBadge(admin.active)}
                </td>
                <td className='whitespace-nowrap px-4 py-4 text-sm'>
                  <div className='flex items-center '>
                    <button
                      onClick={() => {
                        setSelectedTenant(admin);
                        setIsEdit(true);
                      }}
                      className='rounded-xl p-1 text-blue-600 transition-all duration-200 hover:bg-blue-100 hover:text-blue-700'
                    >
                      <Edit size={20} strokeWidth={2} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedTenant(admin);
                        setOpenStudentDeleteDialog(true);
                      }}
                      className='rounded-xl p-1 text-red-600 transition-all duration-200 hover:bg-red-100 hover:text-red-700'
                    >
                      <Trash size={20} strokeWidth={2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.data?.length !== 0 && (
          <Pagination
            currentPage={page}
            handlePageChange={handlePageChange}
            handlePageSizeChange={e => {
              const pageSize = e.target?.value;
              setSize(+pageSize);
            }}
            pageSize={size}
            totalquestions={data?.totaltenants || 0}
          />
        )}
      </div>

      {(!data || data?.data?.length === 0) && (
        <div className='rounded-lg bg-gray-50 py-12 text-center'>
          <p className='text-gray-500'>No Tenant data available</p>
        </div>
      )}
      <CommonDialog
        close={() => setIsEdit(false)}
        isOpen={isEdit}
        dialogTitle={'Edit Tenant'}
        width='md:w-1/2'
      >
        <EditTenant
          setIsEdit={setIsEdit}
          isEdit={isEdit}
          selectedTenant={selectedTenant}
        />
      </CommonDialog>
      <DeleteStudentDialog
        studentName={selectedTenant?.tenantName}
        isOpen={openStudentDeleteDialog}
        onClose={() => setOpenStudentDeleteDialog(false)}
        onDelete={handleDeleteTenant}
        title='Delete Tenant'
      />
    </div>
  );
};

export default AllTenantsAudit;
